<template>
  <div v-if="show" class="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-zinc-900/60 backdrop-blur-sm animate-in fade-in duration-300">
    <Card class="w-full max-w-2xl shadow-2xl border border-border animate-in zoom-in-95 bg-card overflow-hidden self-center">
      <CardHeader class="border-b border-border pb-6 relative">
        <div class="flex items-center justify-between">
          <div class="space-y-1">
            <CardTitle class="text-xl font-black tracking-tight uppercase flex items-center gap-2 text-foreground">
              <GitMerge class="w-5 h-5 text-indigo-500" />
              Fusionner les catégories
            </CardTitle>
            <CardDescription class="font-medium italic text-muted-foreground text-xs text-balance">
              Regroupez les données d'une catégorie vers une autre pour simplifier votre nomenclature.
            </CardDescription>
          </div>
          <Button variant="ghost" size="icon" class="h-8 w-8 text-muted-foreground hover:text-foreground" @click="$emit('close')">
            <X class="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent class="py-10">
        <div class="grid grid-cols-1 md:grid-cols-[1fr,40px,1fr] items-center gap-6 px-4">
          <!-- Source (disappears) -->
          <div class="space-y-4 p-6 rounded-2xl bg-red-500/10 border border-red-500/20 relative overflow-hidden">
            <div class="absolute top-2 right-2 opacity-10">
              <Trash2 class="w-12 h-12 text-red-500" />
            </div>
            <div class="space-y-1">
              <span class="text-[10px] font-black uppercase tracking-widest text-red-400">Cette catégorie disparaîtra</span>
              <p class="text-xl font-black text-red-400 truncate">{{ sourceCategory?.name }}</p>
            </div>
            <div class="flex items-center gap-2 text-[10px] text-red-400/70 font-bold uppercase italic">
              <AlertTriangle class="w-3 h-3" />
              Action irréversible
            </div>
          </div>

          <!-- Arrow -->
          <div class="flex justify-center">
            <div class="w-10 h-10 rounded-full bg-muted border border-border flex items-center justify-center text-muted-foreground shadow-inner">
              <MoveRight class="w-5 h-5" />
            </div>
          </div>

          <!-- Target (persists) -->
          <div class="space-y-4 p-6 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 relative overflow-hidden">
             <div class="absolute top-2 right-2 opacity-10">
              <CheckCircle2 class="w-12 h-12 text-emerald-500" />
            </div>
            <div class="space-y-2">
              <span class="text-[10px] font-black uppercase tracking-widest text-emerald-400">Cette catégorie absorbera</span>
              <div class="relative">
                <select 
                  v-model="targetId"
                  class="w-full bg-muted/30 border border-emerald-500/30 rounded-xl p-3 text-sm font-bold text-foreground focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all appearance-none cursor-pointer"
                >
                  <option value="" disabled class="bg-card">Sélectionner la cible...</option>
                  <option 
                    v-for="cat in availableCategories" 
                    :key="cat.id" 
                    :value="cat.id"
                    class="bg-card font-bold"
                  >
                    {{ cat.displayName }}
                  </option>
                </select>
                <div class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-emerald-400">
                  <ChevronDown class="w-4 h-4" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Info box -->
        <div class="mt-8 mx-4 p-4 rounded-xl bg-muted/30 border border-border text-xs font-medium text-muted-foreground italic flex gap-3 items-center">
          <Info class="w-4 h-4 text-primary shrink-0" />
          <p>Tous les mouvements liés à <span class="text-foreground font-bold">{{ sourceCategory?.name }}</span> seront réattribués à la cible.</p>
        </div>
      </CardContent>

      <CardFooter class="flex justify-end gap-3 bg-muted/30 p-6 border-t border-border">
        <Button variant="ghost" class="font-bold text-muted-foreground hover:bg-muted" @click="$emit('close')">Abandonner</Button>
        <Button 
          variant="finance" 
          :disabled="!targetId || loading" 
          class="min-w-[170px] font-black uppercase tracking-widest h-12 text-xs shadow-xl shadow-black/20" 
          @click="performMerge"
        >
          <template v-if="loading">
            <RefreshCw class="w-4 h-4 mr-2 animate-spin" />
            Fusion...
          </template>
          <template v-else>
            <Check class="w-4 h-4 mr-2" />
            Confirmer Fusion
          </template>
        </Button>
      </CardFooter>
    </Card>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { 
  X, GitMerge, MoveRight, Trash2, CheckCircle2, 
  ChevronDown, AlertTriangle, Info, Check, RefreshCw 
} from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from '@/components/ui/card'
import { useGlobalState } from '../../../composables/useGlobalState'

const props = defineProps({
  show: Boolean,
  sourceCategory: Object,
  allCategories: Array
})

const emit = defineEmits(['close', 'merged'])

const targetId = ref('')
const loading = ref(false)
const { getAuthHeader } = useGlobalState()

const getDescendantIds = (list, parentId) => {
  let ids = []
  const children = list.filter(c => c.parentId === parentId)
  for (const child of children) {
    ids.push(child.id)
    ids = ids.concat(getDescendantIds(list, child.id))
  }
  return ids
}

const availableCategories = computed(() => {
  if (!props.sourceCategory) return []
  
  // 1. Find all descendants of source to exclude them
  const excludedIds = [props.sourceCategory.id, ...getDescendantIds(props.allCategories, props.sourceCategory.id)]
  
  // 2. Filter allowed categories
  const allowed = props.allCategories.filter(c => !excludedIds.includes(c.id))
  
  // 3. Build hierarchical list with display names
  const buildTree = (list, parentId = null, depth = 0) => {
    let result = []
    const level = list.filter(c => c.parentId === parentId)
    for (const c of level) {
      result.push({
        ...c,
        displayName: '\u00A0\u00A0\u00A0'.repeat(depth) + (depth > 0 ? '└─ ' : '') + c.name
      })
      result = result.concat(buildTree(list, c.id, depth + 1))
    }
    return result
  }
  
  return buildTree(allowed)
})

const performMerge = async () => {
  if (!targetId.value) return
  
  loading.value = true
  try {
    const res = await fetch('/api/movements/categories/merge', {
      method: 'POST',
      headers: { 
        ...getAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        sourceId: props.sourceCategory.id,
        targetId: targetId.value
      })
    })
    
    if (res.ok) {
      emit('merged')
    } else {
      const data = await res.json()
      alert(data.error || 'Erreur lors de la fusion')
    }
  } catch (e) {
    alert('Erreur réseau')
  } finally {
    loading.value = false
  }
}
</script>
